import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, LogIn } from 'lucide-react';
import { getMe } from './api.js';
import { completeLogin, hasToken, login } from './auth.js';
import BrandLogo from './components/BrandLogo.js';
import AdminPage from './pages/AdminPage.js';
import DashboardPage from './pages/DashboardPage.js';

function Protected({ children }: { children: React.ReactNode }) {
  if (!hasToken()) return <Navigate to="/login" replace />;
  return children;
}

function LoginPage() {
  return (
    <main className="min-h-screen bg-[#f6f7f9] px-6 py-8">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div className="flex min-w-0 items-center gap-4">
          <BrandLogo />
          <h1 className="text-2xl font-semibold text-slate-950">Portal</h1>
        </div>
        <button
          type="button"
          onClick={() => void login()}
          className="inline-flex items-center gap-2 rounded-md bg-[#117f8a] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0c6871]"
        >
          <LogIn size={18} aria-hidden="true" />
          Login
        </button>
      </div>
    </main>
  );
}

function CallbackPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    completeLogin(location.search)
      .then(async () => {
        const me = await getMe();
        navigate(me.user.isAdmin ? '/admin' : '/dashboard', { replace: true });
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Login fehlgeschlagen.'));
  }, [location.search, navigate]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#f6f7f9] px-6">
      <div className="flex items-center gap-3 text-sm text-slate-700">
        {error ? (
          <span className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</span>
        ) : (
          <>
            <Loader2 size={18} className="animate-spin" aria-hidden="true" />
            Login wird abgeschlossen
          </>
        )}
      </div>
    </main>
  );
}

function HomeRedirect() {
  const me = useQuery({ queryKey: ['me'], queryFn: getMe });

  if (me.isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f6f7f9] px-6">
        <div className="flex items-center gap-3 text-sm text-slate-700">
          <Loader2 size={18} className="animate-spin" aria-hidden="true" />
          Portal wird geladen
        </div>
      </main>
    );
  }

  if (me.isError) return <Navigate to="/login" replace />;
  return <Navigate to={me.data?.user.isAdmin ? '/admin' : '/dashboard'} replace />;
}

function AdminOnly({ children }: { children: React.ReactNode }) {
  const me = useQuery({ queryKey: ['me'], queryFn: getMe });

  if (me.isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f6f7f9] px-6">
        <div className="flex items-center gap-3 text-sm text-slate-700">
          <Loader2 size={18} className="animate-spin" aria-hidden="true" />
          Portal wird geladen
        </div>
      </main>
    );
  }

  if (me.isError) return <Navigate to="/login" replace />;
  if (!me.data?.user.isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/callback" element={<CallbackPage />} />
      <Route
        path="/"
        element={
          <Protected>
            <HomeRedirect />
          </Protected>
        }
      />
      <Route
        path="/dashboard"
        element={
          <Protected>
            <DashboardPage />
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected>
            <AdminOnly>
              <AdminPage />
            </AdminOnly>
          </Protected>
        }
      />
    </Routes>
  );
}
